import React, { useEffect, useRef, useState } from 'react';

// Format seconds into mm:ss — used by both the Duration label below an audio
// source AND the new live "Generating…" elapsed counter.
function fmtMs(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Live elapsed-time chip. Renders only while `running` is true.
// Starts counting from `startedAt` (an EXTERNAL timestamp owned by
// MedStudentClinicPage — passed via `generationStartedAt`). This is
// the bug fix that makes unpark/park mid-generation seamless: the
// parent owns the start timestamp, the timer derives elapsed seconds
// from it, and remounting this component across surfaces continues
// the count instead of resetting to 0:00. Falls back to `Date.now()`
// if `startedAt` is missing (e.g. legacy callers without the parent
// state wired up). The display tick interval is local — only the
// start timestamp needs to survive across remounts.
//
// Past expected-duration thresholds the copy shifts so the doctor
// knows it's fine to walk away — they'll get re-pulled in when it
// lands.
function GeneratingTimer({ running, expectedSec, startedAt = null }) {
  // Anchor derived once per `running`/startedAt flip so a brief
  // re-render doesn't recompute elapsed and visually glitch.
  const [tick, setTick] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!running) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    tickRef.current = setInterval(() => {
      setTick((n) => (n + 1) % 1000000);
    }, 250);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [running]);

  if (!running) return null;

  const anchor = typeof startedAt === 'number' && startedAt > 0 ? startedAt : Date.now();
  const elapsed = (Date.now() - anchor) / 1000;
  const exp = Math.max(15, expectedSec || 45);
  const pct = Math.min(100, (elapsed / exp) * 100);
  let phase = 'go';
  let copy = 'Generating';
  if (elapsed > exp * 1.5) { phase = 'long'; copy = 'Still working'; }
  else if (elapsed > exp * 0.85) { phase = 'almost'; copy = 'Almost there'; }

  return (
    <div
      className={`flex items-center gap-2 text-xs font-semibold rounded-lg px-3 py-1.5 border ${
        phase === 'long'
          ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
          : phase === 'almost'
            ? 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800'
            : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
      }`}
      role="status"
      aria-live="polite"
      aria-label={`${copy}: ${fmtMs(elapsed)} elapsed of about ${fmtMs(exp)} expected`}
      title={`Started ${fmtMs(elapsed)} ago · usually finishes in ~${fmtMs(exp)}`}
    >
      <span className="relative inline-flex w-2 h-2" aria-hidden="true">
        <span className={`absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping ${
          phase === 'long' ? 'bg-amber-500' : phase === 'almost' ? 'bg-violet-500' : 'bg-emerald-500'
        }`} />
        <span className={`relative inline-flex w-2 h-2 rounded-full ${
          phase === 'long' ? 'bg-amber-500' : phase === 'almost' ? 'bg-violet-500' : 'bg-emerald-500'
        }`} />
      </span>
      <span className="tabular-nums">{fmtMs(elapsed)}</span>
      <span className="opacity-75 hidden sm:inline">· {copy}</span>
      <span className="hidden md:inline-flex items-center w-16 h-1 rounded-full bg-current/15 overflow-hidden">
        <span
          className={`h-full ${
            phase === 'long' ? 'bg-amber-500' : phase === 'almost' ? 'bg-violet-500' : 'bg-emerald-500'
          }`}
          style={{ width: `${pct}%`, transition: 'width .25s linear' }}
        />
      </span>
    </div>
  );
}

function formatDuration(value) {
  if (value === null || value === undefined || value === '') return 'Duration unknown';
  if (typeof value === 'string' && value.trim() && !Number.isFinite(Number(value))) {
    return value.trim();
  }
  const rawSeconds = Number(value);
  if (!Number.isFinite(rawSeconds) || rawSeconds <= 0) return 'Duration unknown';
  const seconds = rawSeconds > 12 * 60 * 60 && rawSeconds % 1000 === 0
    ? Math.round(rawSeconds / 1000)
    : Math.round(rawSeconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Skill picker for the Process flow — the SAME per-user preference the AI
// chat's Settings › Skills tab writes (users.ai_chat_settings), surfaced a
// second time here because Generate is where its effect is actually felt: it
// shapes both the Master Note and the suggest-edits proposals. Changing it
// here changes it for the chat too; onSkillChange persists it. Rendered only
// when the parent wires the callback, so callers that don't own the
// preference are unaffected.
function SkillPicker({ activeSkill, skillIntensity, onSkillChange, disabled }) {
  const isCaveman = activeSkill === 'caveman';
  const chip = (on) => [
    'rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50',
    on
      ? 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200'
      : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800/40',
  ].join(' ');
  return (
    <div
      data-testid="process-skill-picker"
      className="rounded-lg border border-slate-200 dark:border-slate-700 p-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400">
          Skill
        </span>
        <button
          type="button" disabled={disabled} aria-pressed={!isCaveman}
          className={chip(!isCaveman)}
          onClick={() => onSkillChange(null, skillIntensity)}
        >
          None
        </button>
        <button
          type="button" disabled={disabled} aria-pressed={isCaveman}
          className={chip(isCaveman)}
          onClick={() => onSkillChange('caveman', skillIntensity)}
        >
          🪨 Caveman
        </button>
        {isCaveman && (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 ml-1">
              Intensity
            </span>
            {['lite', 'full', 'ultra'].map((level) => (
              <button
                key={level}
                type="button" disabled={disabled}
                aria-pressed={skillIntensity === level}
                className={chip(skillIntensity === level)}
                onClick={() => onSkillChange('caveman', level)}
              >
                {level}
              </button>
            ))}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
        {isCaveman
          ? 'Compresses the wording of the generated note and the edit suggestions. Sections, problems, medications, numbers and negations are kept. Saved to your account and shared with AI chat.'
          : 'Default wording. Shared with the AI chat Skills setting.'}
      </p>
    </div>
  );
}

export default function ProcessSourceList({
  groups, selectedIds, expanded, useTemplate,
  onToggleItem, onToggleGroup, onToggleExpand, onToggleTemplate, onResetSelection,
  onSelectOnlyNew, onClearAll,
  onGenerate, generating,
  // Anchor timestamp for the GeneratingTimer — owned by the parent so
  // parking/unparking mid-fetch doesn't reset the clock. Optional; the
  // timer falls back to Date.now() at first render if undefined.
  generationStartedAt = null,
  // Per-user chat "skill" (see SkillPicker). Only rendered when onSkillChange
  // is supplied.
  activeSkill = null,
  skillIntensity = 'full',
  onSkillChange = null,
}) {
  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

  // Estimated time-to-ready for the live counter. Audio dominates (transcription
  // is roughly 0.3x real-time on our pipeline); non-audio sources are cheap
  // text/vision passes. Numbers tuned to feel "honest, slightly conservative":
  // the counter should usually reach 100% before the diff lands, so reaching
  // "Almost there" reads as progress, not as a stall.
  const expectedSec = (() => {
    let total = 10; // base: LLM call + network roundtrip
    for (const g of groups) {
      for (const it of g.items) {
        const idKey = `${it.type}:${it.id}`;
        if (!selectedIds.has(idKey)) continue;
        if (it.type === 'audio') {
          const dur = Number(it.duration) || 0;
          total += 6 + dur * 0.3;
        } else {
          total += 3;
        }
      }
    }
    if (useTemplate) total += 8;
    return Math.round(total);
  })();

  if (totalItems === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">No new sources to process.</div>
    );
  }
  const nothingSelected = selectedIds.size === 0;
  return (
    <div className="flex flex-col gap-3">
      {/* Task #K (Leejoe 2026-06-28): "Use most recent note as template"
          toggle removed — unchecking it broke Generate Diff (the engine
          requires existing_content to do Build-on-Last). Build-on-Last
          IS the value prop; there's no realistic use case for unchecking.
          If/when "use OLDER note as template" becomes a real feature, it
          should be a dropdown picker, not an on/off toggle. */}
      {/* Task #193 — "Process all new" one-click batch. Auto-selects every
          unprocessed source AND fires Generate Diff in the same click, so
          the doctor doesn't have to two-step (Select new only → Generate
          diff). Most common case is "process everything since last visit
          and update the note" — this is that case in one button. */}
      {typeof onSelectOnlyNew === 'function' && (
        <button
          type="button"
          disabled={generating}
          className="text-sm font-semibold px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-md shadow-purple-500/30 hover:from-violet-400 hover:to-purple-500 disabled:opacity-50 disabled:shadow-none w-full"
          onClick={() => { onSelectOnlyNew(); setTimeout(() => onGenerate?.(), 0); }}
          title="Select every unprocessed audio + report and run Generate Diff in one click"
        >
          ✨ Process all new reports and audio
        </button>
      )}
      <div className="border-t border-slate-200 dark:border-slate-700" />
      {groups.map((g) => {
        const groupKey = g.key || g.type;
        const isOpen = expanded.has(groupKey);
        const groupChecked = g.items.length > 0 && g.items.every((i) => selectedIds.has(`${i.type}:${i.id}`));
        return (
          <div key={groupKey} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox" aria-label={g.label} checked={groupChecked}
                onChange={() => onToggleGroup(groupKey)}
              />
              <span className="text-sm font-semibold">{g.label}</span>
              <span className="text-xs text-emerald-600 dark:text-emerald-400">{g.newCount} new</span>
              <button
                type="button" className="ml-auto text-xs text-slate-500"
                onClick={() => onToggleExpand(groupKey)}
              >
                {isOpen ? 'collapse' : 'expand'}
              </button>
            </div>
            {isOpen && (
              <ul className="mt-2 flex flex-col gap-1 pl-6">
                {g.items.map((i) => (
                  <li key={`${i.type}:${i.id}`} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox" aria-label={i.label}
                      checked={selectedIds.has(`${i.type}:${i.id}`)}
                      onChange={() => onToggleItem(i.type, i.id)}
                    />
                    <span className={i.processed ? 'text-slate-400' : ''}>{i.label}</span>
                    {i.type === 'audio' && (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Duration: {formatDuration(i.duration)}
                      </span>
                    )}
                    {i.processed && <span className="text-xs text-slate-400">processed</span>}
                  </li>
                ))}
                {g.items.length === 0 && <li className="text-xs text-slate-400">None</li>}
              </ul>
            )}
          </div>
        );
      })}
      {typeof onSkillChange === 'function' && (
        <SkillPicker
          activeSkill={activeSkill}
          skillIntensity={skillIntensity}
          onSkillChange={onSkillChange}
          disabled={generating}
        />
      )}
      {/* Sticky action bar — Generate Diff stays visible regardless of how
          many sources the user has scrolled past. Surfaced by Janu 2026-06-27
          during dogfood call: "Generate diff was so far down I could not see
          it" + "should be something that doesn't move, like the recorder
          button." sticky bottom-0 keeps it pinned to the bottom of the modal
          scroll container; the background + border + shadow visually
          separate it from the source list above so it doesn't feel like part
          of the scrolling content. */}
      <div
        className={[
          'sticky bottom-0 z-10 -mx-4 px-4 py-3 mt-2 flex items-center justify-end gap-2 flex-wrap',
          'bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm',
          'border-t border-slate-200 dark:border-slate-700',
          'shadow-[0_-4px_12px_rgba(15,23,42,0.06)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]',
        ].join(' ')}
      >
        <GeneratingTimer running={generating} expectedSec={expectedSec} startedAt={generationStartedAt} />
        {/* Task #151 + Leejoe 2026-06-28: kill the green "Select new only"
            chip. The purple "Process all new reports and audio" button
            already does Select-new-only + immediate Generate in one click
            (KISS — same intent, more visible). Clear all stays for the
            hand-pick escape hatch. */}
        {typeof onClearAll === 'function' && (
          <button
            type="button" disabled={generating}
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/40 disabled:opacity-50"
            onClick={onClearAll}
            title="Uncheck every source so you can hand-pick"
          >
            ☐ Clear all
          </button>
        )}
        <button
          type="button" disabled={generating || nothingSelected}
          className="text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/30 hover:from-emerald-400 hover:to-teal-500 disabled:opacity-50 disabled:shadow-none"
          onClick={onGenerate}
        >
          {generating ? 'Generating…' : useTemplate ? '⚡ Generate diff' : 'Ask AI Chat'}
        </button>
      </div>
    </div>
  );
}
